/**
 * `/admin/finances` — what we pay for.
 *
 * Itemised vendor subscriptions (manually maintained — most of our vendors
 * expose no billing API; see the migration header for which could be
 * auto-fetched later), the itemised monthly total, and its drift from the
 * `platform_fixed_monthly` rate the unit-economics totals charge. One click
 * applies the itemised total onto that rate.
 *
 * Gated by the parent `_app/admin` AdminGate.
 */
import { Island } from '@/components/shell/Island';
import {
  type AdminUnitEconomicsTotals,
  type AdminVendorSubscription,
  useAdminFinances,
  useAdminUnitEconomics,
  useUpdateFinances,
  useUpdateUnitEconomicsRates,
} from '@/lib/queries/admin';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/admin/finances')({
  component: AdminFinancesPage,
});

const WINDOWS = [7, 30, 90] as const;

// USD with adaptive precision: whole dollars read at 2dp; sub-dollar COGS
// needs 4dp to not render as $0.00. Same rule as the /admin org table.
function fmtUsd(n: number): string {
  const abs = Math.abs(n);
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: abs > 0 && abs < 1 ? 4 : 2,
  });
}

function fmtPct(n: number | null): string {
  return n === null ? '—' : `${n.toFixed(1)}%`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function StatCard({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'good' | 'bad';
}): ReactElement {
  const valueColor =
    tone === 'good'
      ? 'text-[var(--color-success,#0e9f6e)]'
      : tone === 'bad'
        ? 'text-[var(--color-danger)]'
        : '';
  return (
    <div className="rounded-lg border border-[var(--color-line)] bg-[var(--color-paper)] px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-[var(--color-mute)]">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${valueColor}`}>{value}</div>
      {note ? <div className="mt-0.5 text-[11px] text-[var(--color-mute)]">{note}</div> : null}
    </div>
  );
}

/** One P&L row — used for both the all-time tally and the window. */
function TotalsCards({
  totals,
  trialLabel,
}: {
  totals: AdminUnitEconomicsTotals;
  trialLabel: string;
}): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <StatCard label="Revenue" value={fmtUsd(totals.revenue)} />
      <StatCard label="Cost to serve" value={fmtUsd(totals.cost)} />
      <StatCard
        label="Gross profit"
        value={fmtUsd(totals.gross_profit)}
        tone={totals.gross_profit >= 0 ? 'good' : 'bad'}
        note={`margin ${fmtPct(totals.gross_margin_pct)}`}
      />
      <StatCard label="Platform fixed" value={fmtUsd(totals.platform_fixed)} />
      <StatCard
        label="Net profit"
        value={fmtUsd(totals.net_profit)}
        tone={totals.net_profit >= 0 ? 'good' : 'bad'}
      />
      <StatCard
        label={trialLabel}
        value={fmtUsd(totals.trial.cost)}
        note={`${totals.trial.active_orgs} active · ${totals.trial.lapsed_orgs} lapsed · ${totals.trial.converted_orgs} converted`}
      />
    </div>
  );
}

const inputCls =
  'h-8 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2 text-sm outline-none focus:border-[var(--color-line-strong)]';

function AddVendorForm(): ReactElement {
  const [vendor, setVendor] = useState('');
  const [planName, setPlanName] = useState('');
  const [cost, setCost] = useState('');
  const [billing, setBilling] = useState<'fixed' | 'usage'>('fixed');
  const update = useUpdateFinances();
  const valid = /^[a-z0-9][a-z0-9-_]*$/.test(vendor.trim()) && Number.parseFloat(cost) >= 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || update.isPending) return;
        update.mutate(
          {
            upserts: [
              {
                vendor: vendor.trim(),
                plan_name: planName.trim(),
                monthly_cost_usd: Number.parseFloat(cost),
                billing,
              },
            ],
          },
          {
            onSuccess: () => {
              setVendor('');
              setPlanName('');
              setCost('');
            },
          },
        );
      }}
      className="flex flex-wrap items-end gap-2 rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] p-3"
    >
      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase tracking-wide text-[var(--color-mute)]">Vendor</span>
        <input
          type="text"
          value={vendor}
          onChange={(e) => setVendor(e.target.value.toLowerCase())}
          placeholder="sentry"
          className={`${inputCls} w-36`}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase tracking-wide text-[var(--color-mute)]">Plan</span>
        <input
          type="text"
          value={planName}
          onChange={(e) => setPlanName(e.target.value)}
          placeholder="Team"
          className={`${inputCls} w-32`}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase tracking-wide text-[var(--color-mute)]">$/mo</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          placeholder="26.00"
          className={`${inputCls} w-24 text-right tabular-nums`}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase tracking-wide text-[var(--color-mute)]">
          Billing
        </span>
        <select
          value={billing}
          onChange={(e) => setBilling(e.target.value as 'fixed' | 'usage')}
          className={`${inputCls} w-24`}
        >
          <option value="fixed">fixed</option>
          <option value="usage">usage</option>
        </select>
      </label>
      <button
        type="submit"
        disabled={!valid || update.isPending}
        className="h-8 rounded-md bg-[var(--color-ink)] px-3 font-medium text-[var(--color-paper)] text-xs hover:opacity-90 disabled:opacity-40"
      >
        {update.isPending ? 'Adding…' : 'Add vendor'}
      </button>
      {update.error ? (
        <span className="text-xs text-[var(--color-danger)]">
          {String((update.error as Error)?.message ?? update.error)}
        </span>
      ) : null}
    </form>
  );
}

function SubscriptionRow({ sub }: { sub: AdminVendorSubscription }): ReactElement {
  const [cost, setCost] = useState<string | null>(null);
  const update = useUpdateFinances();
  const edited = cost !== null && Number.parseFloat(cost) !== sub.monthly_cost_usd;
  const parsed = cost === null ? sub.monthly_cost_usd : Number.parseFloat(cost);

  return (
    <tr className="border-b border-[var(--color-line-soft)]">
      <td className="py-2 pr-4 font-medium">{sub.vendor}</td>
      <td className="py-2 pr-4 text-xs text-[var(--color-mute)]">{sub.plan_name || '—'}</td>
      <td className="py-2 pr-4 text-xs">
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${
            sub.billing === 'fixed'
              ? 'bg-[var(--color-paper-alt)] text-[var(--color-mute)]'
              : 'bg-[var(--color-accent-brand)]/10 text-[var(--color-accent-brand)]'
          }`}
        >
          {sub.billing}
        </span>
      </td>
      <td className="py-2 pr-4 text-right">
        <input
          type="number"
          min="0"
          step="0.01"
          value={cost ?? String(sub.monthly_cost_usd)}
          onChange={(e) => setCost(e.target.value)}
          aria-label={`${sub.vendor} monthly cost`}
          className={`${inputCls} w-28 text-right tabular-nums ${edited ? 'border-[var(--color-accent-brand)]' : ''}`}
        />
      </td>
      <td className="max-w-[360px] py-2 pr-4 text-xs text-[var(--color-mute)]">{sub.notes}</td>
      <td className="py-2 pr-4 text-xs text-[var(--color-mute)]">{fmtDate(sub.updated_at)}</td>
      <td className="py-2 pr-0 text-right">
        <div className="flex justify-end gap-1.5">
          <button
            type="button"
            disabled={!edited || Number.isNaN(parsed) || parsed < 0 || update.isPending}
            onClick={() =>
              update.mutate(
                { upserts: [{ vendor: sub.vendor, monthly_cost_usd: parsed }] },
                { onSuccess: () => setCost(null) },
              )
            }
            className="h-7 rounded-md border border-[var(--color-line)] px-2 text-xs hover:bg-[var(--color-paper-alt)] disabled:opacity-40"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            disabled={update.isPending}
            onClick={() => {
              if (window.confirm(`Remove ${sub.vendor} from the subscription list?`)) {
                update.mutate({ deletes: [sub.vendor] });
              }
            }}
            className="h-7 rounded-md border border-[var(--color-line)] px-2 text-xs text-[var(--color-danger)] hover:bg-[var(--color-paper-alt)]"
          >
            Remove
          </button>
        </div>
      </td>
    </tr>
  );
}

function AdminFinancesPage(): ReactElement {
  const q = useAdminFinances();
  const rates = useUpdateUnitEconomicsRates();
  const [showAdd, setShowAdd] = useState(false);
  const [days, setDays] = useState<number>(30);
  const econQ = useAdminUnitEconomics(days);
  const econ = econQ.data;

  const d = q.data;
  const drift = d ? d.total_monthly_usd - d.platform_fixed_monthly : 0;

  return (
    <Island
      breadcrumb={[{ label: 'Admin', href: '/admin' }, { label: 'Finances' }]}
      actions={
        <>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-7 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2 text-xs outline-none focus:border-[var(--color-line-strong)]"
            aria-label="P&L window"
          >
            {WINDOWS.map((w) => (
              <option key={w} value={w}>
                Last {w} days
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowAdd((v) => !v)}
            className="h-7 rounded-md border border-[var(--color-line)] px-2.5 text-xs hover:bg-[var(--color-paper-alt)]"
          >
            {showAdd ? 'Close' : 'Add vendor'}
          </button>
        </>
      }
    >
      {q.isLoading ? (
        <p className="py-8 text-center text-sm text-[var(--color-mute)]">Loading…</p>
      ) : q.error ? (
        <p className="py-8 text-center text-sm text-[var(--color-danger)]">
          {String((q.error as Error)?.message ?? q.error)}
        </p>
      ) : d ? (
        <div className="flex flex-col gap-4">
          {econ && !econ.events_available ? (
            <p className="rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-3 py-2 text-xs text-[var(--color-mute)]">
              Tinybird is unreachable — RUM volume and its cost are excluded from every P&amp;L
              figure right now.
            </p>
          ) : null}
          {econ ? (
            <>
              <div>
                <h2 className="mb-2 text-xs uppercase tracking-wide text-[var(--color-mute)]">
                  Running tally — all time
                </h2>
                <TotalsCards totals={econ.all_time} trialLabel="Trials cost us" />
              </div>
              <div>
                <h2 className="mb-2 text-xs uppercase tracking-wide text-[var(--color-mute)]">
                  Last {econ.window_days} days
                </h2>
                <TotalsCards totals={econ.totals} trialLabel="Trial burn" />
              </div>
            </>
          ) : null}

          <h2 className="mt-2 text-xs uppercase tracking-wide text-[var(--color-mute)]">
            Subscriptions
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard label="Itemised monthly spend" value={fmtUsd(d.total_monthly_usd)} />
            <StatCard
              label="Platform fixed (economics)"
              value={fmtUsd(d.platform_fixed_monthly)}
              note="the rate the unit-economics totals charge"
            />
            <StatCard
              label="Drift"
              value={fmtUsd(drift)}
              tone={Math.abs(drift) < 1 ? 'good' : 'bad'}
              note="itemised total minus the fixed-floor rate"
            />
          </div>

          {Math.abs(drift) >= 1 ? (
            <div className="flex items-center gap-3 rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-3 py-2 text-xs">
              <span>
                The economics totals still charge {fmtUsd(d.platform_fixed_monthly)}/mo as the fixed
                floor.
              </span>
              <button
                type="button"
                disabled={rates.isPending}
                onClick={() =>
                  rates.mutate({
                    unit_costs: [
                      {
                        metric: 'platform_fixed_monthly',
                        unit_cost_usd: d.total_monthly_usd,
                      },
                    ],
                  })
                }
                className="h-7 rounded-md bg-[var(--color-ink)] px-2.5 font-medium text-[var(--color-paper)] hover:opacity-90 disabled:opacity-40"
              >
                {rates.isPending
                  ? 'Applying…'
                  : `Apply ${fmtUsd(d.total_monthly_usd)} to platform fixed`}
              </button>
              {rates.error ? (
                <span className="text-[var(--color-danger)]">
                  {String((rates.error as Error)?.message ?? rates.error)}
                </span>
              ) : null}
            </div>
          ) : null}

          {showAdd ? <AddVendorForm /> : null}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-line)] text-left text-xs text-[var(--color-mute)]">
                  <th className="py-2 pr-4 font-medium">Vendor</th>
                  <th className="py-2 pr-4 font-medium">Plan</th>
                  <th className="py-2 pr-4 font-medium">Billing</th>
                  <th className="py-2 pr-4 text-right font-medium">$/month</th>
                  <th className="py-2 pr-4 font-medium">Notes</th>
                  <th className="py-2 pr-4 font-medium">Updated</th>
                  <th className="py-2 pr-0" />
                </tr>
              </thead>
              <tbody>
                {d.subscriptions.map((s) => (
                  <SubscriptionRow key={s.vendor} sub={s} />
                ))}
              </tbody>
            </table>
            {d.subscriptions.length === 0 ? (
              <p className="py-8 text-center text-sm text-[var(--color-mute)]">
                No subscriptions recorded.
              </p>
            ) : null}
          </div>

          <p className="text-[11px] text-[var(--color-mute)]">
            Amounts are maintained by hand — Cloudflare, Supabase and Clerk expose no billing API
            (invoice emails only). Usage-billed rows hold the latest typical month; Railway and GCP
            spend could be auto-fetched later (Railway usage GraphQL / BigQuery billing export).
          </p>
        </div>
      ) : null}
    </Island>
  );
}
